import { useEffect, useState } from "react";
import { useGetInventory } from "../../../helpers/queries/scans/useGetInventory";
import { formatFullDate, formatTime } from "../../../lib/utils";
import { Spinner } from "../../../components/Spinner";
import { FiGlobe } from "react-icons/fi";
import { Button } from "../../../components/ui/button";
import empty from "../../../assets/images/empty_assets.png";
import { usePaginationSync } from "../../../hooks/usePaginationSync";
import { PaginationComponent } from "../../../components/Pagination";
import { useGetUserRole } from "../../../hooks/useGetUserRole";
import UploadInventoryModal from "./modal/UploadInventory";
// import { Check, Minus } from "lucide-react";
// import { Checkbox } from "../../../components/ui/checkbox";
import CreateAssetGroupModal from "./modal/CreateAssetGroupModal";
import GroupedAsset from "./GroupedAsset";
import AddTargetModal from "../Targets/modals/AddTargetModal";
import { FaPlus } from "react-icons/fa6";
import Unknown from "./Unknown";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteInventory } from "../../../helpers/api";
import { toast } from "react-toastify";
import DeleteModal from "../../../components/modals/DeleteModal";
import DataTable from "../../../components/DataTable";
import SelectionActionBar from "../../../components/SelectionActionBar";

const Inventory = () => {
  const [lastPage, setLastPage] = useState(1);
  const { currentPage, updatePage } = usePaginationSync(lastPage);
  const { isLoading, isSuccess, isError, data } = useGetInventory(currentPage);
  const [openDeleteModal, setOpenDeleteModal] = useState(null);

  useEffect(() => {
    setLastPage(data?.pagination?.last_page);
  }, [data]);

  const { viewer } = useGetUserRole();

  const [selectedIds, setSelectedIds] = useState([]);

  const handleDeleteModal = () => {
  setOpenDeleteModal(null);
  setSelectedIds([]); // clear selection when modal closes
};


  // allIds always reflects current results
  // const allIds = useMemo(
  //   () => data?.data?.map((r) => r.id) || [],
  //   [data?.data]
  // );

  // const isIndeterminate =
  //   selectedIds.length > 0 && selectedIds.length < allIds.length;
  // const isAllSelected = selectedIds.length === allIds.length;

  // const toggleSelectAll = () => {
  //   if (isIndeterminate || isAllSelected) {
  //     setSelectedIds([]); // clear all
  //   } else {
  //     setSelectedIds(allIds); // select all
  //   }
  // };

  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { key: "all", label: "All" },
    { key: "groups", label: "Asset Groups" },
    { key: "unknown", label: "Unknown" },
  ];

  const queryClient = useQueryClient();
  const { mutate: deleteAsset, isPending: deletePending } = useMutation({
    mutationFn: (id) => deleteInventory(id),
    onSuccess: (data) => {
      toast(data.message);
      handleDeleteModal();
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete inventory");
    },
  });

  const handleDelete = () => {
    deleteAsset(selectedIds);
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Hostname",
      cell: ({ row }) => (
        <span className=" uppercase">{row.original.name}</span>
      ),
    },
    {
      accessorKey: "ip_address",
      header: "Last Known IP Address",
      cell: ({ row }) => <span>{row.original.ip_address}</span>,
    },
    {
      accessorKey: "asset_group",
      header: "Asset Group",
      cell: ({ row }) => <span>{row.original.asset_group}</span>,
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => (
        <div className=" text-left text-grey">
          <span className="text-neutral800">
            {formatFullDate(row.original.created_at)},
          </span>{" "}
          <span className="text-neutral600">
            {formatTime(row.original.created_at)}
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w-full">
        <div className=" w-full lg:block flex justify-between items-center py-6 lg:px-10 md:px-5 px-4 bg-white border-b mb-4">
          <div className="w-full flex lg:flex-row flex-col justify-between lg:items-center gap-4 mb-1">
            <div className="flex gap-3 items-center">
              <div>
                <div className="w-[50px] h-[50px] rounded-full border border-neutral300 flex justify-center items-center">
                  <FiGlobe className="text-neutral700 text-[20px]" />
                </div>
              </div>
              <div>
                <p className="text-darkGrey font-semibold md:block hidden">
                  Inventory
                </p>
                <p className="text-neutral600 md:text-base text-sm">
                  Manage your assets - domains and IP addresses
                </p>
              </div>
            </div>

            {!viewer && <UploadInventoryModal />}
          </div>
        </div>

        <div className="flex md:flex-row flex-col-reverse gap-4 md:items-center justify-between mx-4">
          <div className="w-[350px] rounded-lg bg-neutral200 h-[40px] flex py-1 relative overflow-hidden">
            <div
              className={`absolute top-1 left-1  h-[calc(100%-8px)] rounded-lg bg-white transition-all duration-500`}
              style={{
                width: `${97.5 / tabs.length}%`,
                transform: `translateX(${
                  tabs.findIndex((tab) => tab.key === activeTab) * 100
                }%)`,
              }}
            ></div>

            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`flex-1 relative z-10 text-center font-medium transition-colors duration-300 cursor-pointer ${
                  activeTab === tab.key ? "text-neutral900" : "text-neutral700"
                }`}
                onMouseEnter={() => {
                  setActiveTab(tab.key);
                }}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {!viewer && (
            <AddTargetModal
              triggerLabel="Create asset group"
              modalTitle="Create asset group"
              is_asset_group={true}
              icon={<FaPlus size={16} />}
            />
          )}
        </div>

        {isLoading && !isSuccess && (
          <div className="h-[55vh] w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && !isLoading && (
          <div className="h-[45vh] w-full flex justify-center flex-col items-center">
            <p className="font-semibold text-xl text-center">
              There was an error getting the data
            </p>
          </div>
        )}

        {!isLoading && isSuccess && data?.data?.length > 0 && (
          <>
            {activeTab === "all" && (
              <div>
                {data?.data?.length > 0 && (
                  <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
                    {/* {!viewer && (
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          selectedIds.length > 0
                            ? "max-h-20 opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="flex items-center justify-between bg-neutral-100 border rounded-lg px-3 xl:px-4 py-2 mb-2">
                          <div className=" flex items-center gap-4">
                            <button
                              onClick={toggleSelectAll}
                              className="w-4 h-4 flex items-center justify-center border rounded bg-[#0f172a]"
                            >
                              {isAllSelected ? (
                                <Check size={14} className="text-white" />
                              ) : isIndeterminate ? (
                                <Minus size={14} className="text-white" />
                              ) : null}
                            </button>

                            <div className="flex flex-col gap-1">
                              <span className="text-sm font-medium text-neutral-700">
                                {selectedIds.length}{" "}
                                {selectedIds.length === 1
                                  ? "target"
                                  : "targets"}{" "}
                                selected
                              </span>

                              <Button
                                onClick={() => setSelectedIds(allIds)}
                                className="text-sm cursor-pointer text-blue-500 hover:text-[#0f172a] font-medium"
                                variant={"ghost"}
                                size={"ghost"}
                                disabled={isAllSelected}
                              >
                                Select All
                              </Button>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <CreateAssetGroupModal selectedIds={selectedIds} />

                            <DeleteModal
                              onOpenChange={setOpenDeleteModal}
                              open={openDeleteModal}
                              title="Delete asset"
                              handleDelete={handleDelete}
                              loading={deletePending}
                            >
                              <Button
                                className="text-sm  text-red-500 hover:text-red-700 font-medium"
                                variant={"ghost"}
                                size={"ghost"}
                              >
                                Delete
                              </Button>
                            </DeleteModal>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="overflow-x-auto">
                      <div className="w-full border min-w-[1100px] border-neutral300 rounded-lg text-sm">
                        <div className="flex justify-between text-left px-3 xl:px-6 py-2 bg-neutral3 rounded-t-lg border-b text-neutral700 font-medium w-full">
                          <div className="w-4 mr-2"></div>
                          <span className="flex-1">Hostname</span>
                          <span className="flex-1">Last Known IP Address</span>
                          <span className="flex-1">Asset Group</span>
                          <span className="flex-1">Created At</span>
                        </div>

                        {data?.data?.map((item, index) => {
                          const isChecked = selectedIds.includes(item.id);
                          return (
                            <div
                              key={index}
                              className="flex justify-between text-left px-3 xl:px-6 py-4 border-b text-neutral700 w-full"
                            >
                              <div className="flex items-center justify-center w-4 mr-2">
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={() => {
                                    setSelectedIds((prev) =>
                                      prev.includes(item.id)
                                        ? prev.filter((id) => id !== item.id)
                                        : [...prev, item.id]
                                    );
                                  }}
                                />
                              </div>
                              <span className="flex-1 uppercase">
                                {item.name}
                              </span>
                              <span className="flex-1">{item.ip_address}</span>
                              <span className="flex-1">{item.asset_group}</span>
                              <div className="flex-1 text-left text-grey">
                                <span className="text-neutral800">
                                  {formatFullDate(item.created_at)},
                                </span>{" "}
                                <span className="text-neutral600">
                                  {formatTime(item.created_at)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                     
                    </div> */}

                    <SelectionActionBar
                      count={selectedIds.length}
                      label="assets"
                      actions={
                        <div className="flex items-center gap-4">
                          <CreateAssetGroupModal selectedIds={selectedIds} setSelectedIds={setSelectedIds}/>

                          <DeleteModal
                            onOpenChange={setOpenDeleteModal}
                            open={openDeleteModal}
                            title="Delete asset"
                            handleDelete={handleDelete}
                            loading={deletePending}
                          >
                            <Button
                              className="text-sm  text-red-500 hover:text-red-700 font-medium"
                              variant={"ghost"}
                              size={"ghost"}
                            >
                              Delete
                            </Button>
                          </DeleteModal>
                        </div>
                      }
                    />
                    <DataTable
                      columns={columns}
                      data={data?.data}
                      enableRowSelection
                      onSelectionChange={setSelectedIds}
                    />

                     <PaginationComponent
                        lastPage={data?.pagination?.last_page}
                        currentPage={currentPage}
                        handlePageChange={updatePage}
                      />
                  </div>
                ) }
              </div>
            )}

            {activeTab === "groups" && <GroupedAsset />}
            {activeTab === "unknown" && <Unknown />}
          </>
        )}

        {data?.data?.length === 0 && !isLoading && isSuccess && (
          <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
            <img src={empty} alt="empty" />
            <p className=" font-semibold my-2 text-darkGrey">
              No inventory added yet
            </p>
            <p className="text-neutral600 text-sm max-w-[360px] mb-4">
              Start by adding an inventory. This can be domains or IP addresses
              you'd like to monitor for vulnerabilities.
            </p>
            {/* {role && role !== "Viewer" && <AddTargetModal />} */}
          </div>
        )}
      </div>
    </>
  );
};

export default Inventory;
